class WatchDesigner {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    this.config = {
      // canvasWidth: 600,
      // canvasHeight: 600,
      maxFileSize: 5 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  
      // Card dimensions in mm (ground truth)
      cardDimensions: {
        width: 63.5,  // Standard TCG card width
        height: 88.9  // Standard TCG card height
      },
      
      // Watch display dimensions in mm (ground truth)
      watchModels: {
        'casio-f91w': { 
          width: 20.73,   // mm - width of the paper plate behind the display screen; the display itself is 19.05mm wide
          height: 11.43   // mm - height of the paper plate behind the display screen; the display itself is 9.35mm tall
        },
        'casio-illuminator': { 
          width: 21,   // mm - height of the paper plate behind the display screen; the display itself is 19.5mm wide
          height: 14.18   // mm - height of the paper plate behind the display screen; the display itself is 12.85mm tall
        }
      },

      // overlayStyle: {
      //   fillRGBA: 'rgba(0, 0, 0, 0.3)',
      //   outlineColor: '#030201',
      //   outlineCssPx: 20 // <-- THIS is the CSS thickness used in the live widget
      // },

      apiEndpoint: 'https://watch-designer-api.watchslabs.workers.dev'
    };

    this.state = {
      uploadedImage: null,
      imageData: null,
      // template: 'tcg-card',
      watchModel: 'casio-f91w',
      // rectangleX: 0.35,
      // rectangleY: 0.4,
      rectangleRotation: 0, // Add this line
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0
    };

    this.init();
  }

  init() {
    this.createHTML();
    this.setupEventListeners();
  }

  createHTML() {
    this.container.innerHTML = `
      <div class="watch-designer">
        <form id="wd-form" class="watch-designer__form">

          <!-- Widget Title -->
          <div class="watch-designer__header">
            <h2 class="watch-designer__title">
              Custom Design Editor
            </h2>
          </div>
          
          <!-- Honeypot field -->
          <input type="text" name="website" class="watch-designer__hp" tabindex="-1" autocomplete="off">
          
          <!-- Step 1: Upload Image -->
          <div class="watch-designer__section">
            <label class="watch-designer__label">
              1. Upload Card Image <span class="watch-designer__required">*</span>
            </label>

            <input
              type="file"
              id="wd-file-input"
              class="watch-designer__file-input"
              accept="image/jpeg,image/png,image/webp"
            >

            <label for="wd-file-input" class="watch-designer__upload-btn">Choose Image</label>

            <p class="watch-designer__help-text">
              Upload a clear image of the card, cropped to the card&rsquo;s edges — images from TCGPlayer work best.<br>
              JPG, PNG, or WEBP. Max 5MB.
            </p>

            <div id="wd-file-error" class="watch-designer__error wd-hidden"></div>
          </div>

          <!-- Step 2: Source Link (REQUIRED) -->
          <div class="watch-designer__section">
            <label class="watch-designer__label" for="wd-source-link">
              2. Source Link <span class="watch-designer__required">*</span>
            </label>
            <input 
              type="text" 
              id="wd-source-link" 
              name="sourceLink"
              class="watch-designer__input" 
              placeholder="TCGPlayer URL"
              required
            >
            <div id="wd-source-error" class="watch-designer__error wd-hidden"></div>
          </div>

          <!-- Step 3: Customer Name -->
          <div class="watch-designer__section">
            <label class="watch-designer__label" for="wd-name">
              3. Your Name <span class="watch-designer__required">*</span>
            </label>
            <input
              type="text"
              id="wd-name"
              name="name"
              class="watch-designer__input"
              placeholder="John Doe"
              required
            >
          </div>

          <!-- Step 4: Customer Email -->
          <div class="watch-designer__section">
            <label class="watch-designer__label" for="wd-email">
              4. Your Email <span class="watch-designer__required">*</span>
            </label>
            <input
              type="email"
              id="wd-email"
              name="email"
              class="watch-designer__input"
              placeholder="you@example.com"
              required
            >
          </div>

          <!-- Step 5: Template Type
          <div class="watch-designer__section">
            <label class="watch-designer__label" for="wd-template">
              5. Template Type
            </label>
            <select id="wd-template" name="template" class="watch-designer__select">
              <option value="tcg_card">TCG Card</option>
              <option value="manga">Manga Panel</option>
            </select>
            <p class="watch-designer__help-text">
              <strong>TCG Card:</strong> Pokémon, MTG, One Piece, Digimon, Lorcana, etc.<br>
              <strong>Manga Panel:</strong> Cropped from manga pages or panels
            </p>
          </div>
          -->

          <!-- Step 5: Watch Model -->
          <div class="watch-designer__section">
            <label class="watch-designer__label" for="wd-watch-model">
              5. Watch Model <span class="watch-designer__required">*</span>
            </label>
            <select id="wd-watch-model" name="watchModel" class="watch-designer__select">
              <option value="casio-f91w">Casio F-91W</option>
              <option value="casio-illuminator">Casio Illuminator</option>
              required
            </select>
          </div>

          <!-- Step 6: Position Watch Window -->
          <div class="watch-designer__section">
            <label class="watch-designer__label">
              6. Position Watch Window <span class="watch-designer__required">*</span>
            </label>

            <p class="watch-designer__help-text">
              Drag the gray rectangle to position the watch window. Use the rotation slider below to rotate.<br>The opaque black border will not be part of the final product.<br>
              <strong><em>
                Note: The digital screen slightly darkens the card beneath it, which can make darker artwork hard to see.
                The gray rectangle in the editor approximates how it will look in the watch.
              </em></strong>
            </p>

            <div class="watch-designer__canvas-container" id="wd-frame-container">
              <img id="wd-uploaded-image" class="watch-designer__canvas" style="display: none;" alt="Uploaded card">
              <div id="wd-overlay" class="watch-designer__overlay" style="display: none;"></div>
            </div>

            <div style="margin-top: 12px;">
              <label style="display: block; font-weight: 600; margin-bottom: 8px; font-size: 14px;">
                Rotation: <span id="wd-rotation-display">0°</span>
              </label>
              <input 
                type="range"
                id="wd-rotation-slider"
                min="0"
                max="360"
                value="0"
                step="1"
                style="width: 100%; cursor: pointer; -webkit-appearance: none; appearance: none; background: #e5e7eb; height: 8px; border-radius: 4px; outline: none;"
                required
              >
            </div>

            <button
              type="button"
              id="wd-download-btn"
              class="watch-designer__download-btn"
              style="margin-top: 16px; width: 100%; padding: 16px; background: #5B74E0; color: white; border: none; border-radius: 6px; font-size: 16px; font-weight: 700; cursor: pointer;"
              disabled
            >
              Download Preview Image
            </button>
          </div>

          <!-- Optional Notes -->
          <div class="watch-designer__section">
            <label class="watch-designer__label" for="wd-notes">
              Additional Notes (Optional)
            </label>
            <textarea 
              id="wd-notes" 
              name="notes"
              class="watch-designer__textarea" 
              placeholder="Any special requests or details..."
            ></textarea>
          </div>

          <!-- Submit Button -->
          <div class="watch-designer__section">
            <button type="submit" id="wd-submit-btn" class="watch-designer__submit-btn" disabled>
              Submit for Quote
            </button>
          </div>

        </form>

        <!-- Confirmation Message (hidden initially) -->
        <div id="wd-confirmation" class="watch-designer__confirmation wd-hidden">
          <h3>✓ Quote Request Submitted!</h3>
          <p>Thanks! We'll reach out within 2–3 business days with a quote.</p>
        </div>
      </div>
    `;

      this.frameContainer = document.getElementById('wd-frame-container');
      this.uploadedImage = document.getElementById('wd-uploaded-image');
      this.overlay = document.getElementById('wd-overlay');
  }

  setupEventListeners() {
    // File upload
    document.getElementById('wd-file-input').addEventListener('change', (e) => this.handleFileUpload(e));

    // Template/Watch model changes
    // document.getElementById('wd-template').addEventListener('change', (e) => {
    //   this.state.template = e.target.value;
    // });

    document.getElementById('wd-watch-model').addEventListener('change', (e) => {
      this.state.watchModel = e.target.value;
      this.recalculateOverlay(); // Recalc on model change
    });

    // // Overlay drag events (mouse)
    // this.overlay.addEventListener('mousedown', (e) => this.startDrag(e));
    // document.addEventListener('mousemove', (e) => this.drag(e));
    // document.addEventListener('mouseup', () => this.endDrag());

    // // Overlay drag events (touch)
    // this.overlay.addEventListener('touchstart', (e) => this.startDrag(e), { passive: false });
    // document.addEventListener('touchmove', (e) => this.drag(e), { passive: false });
    // document.addEventListener('touchend', () => this.endDrag());

    // Simplified overlay drag events (switches to pointer events instead of mouse + touch)
    this.overlay.addEventListener('pointerdown', (e) => this.startDrag(e));
    document.addEventListener('pointermove', (e) => this.drag(e));
    document.addEventListener('pointerup', (e) => this.endDrag(e));

    // Rotation slider
    document.getElementById('wd-rotation-slider').addEventListener('input', (e) => {
      this.state.rectangleRotation = parseInt(e.target.value);
      document.getElementById('wd-rotation-display').textContent = `${this.state.rectangleRotation}°`;
      this.overlay.style.transform = `rotate(${this.state.rectangleRotation}deg)`;
    });

    // Download button
    document.getElementById('wd-download-btn').addEventListener('click', () => this.downloadPreview());

    // Form validation on input
    document.getElementById('wd-source-link').addEventListener('input', () => this.validateForm());
    document.getElementById('wd-name').addEventListener('input', () => this.validateForm());
    document.getElementById('wd-email').addEventListener('input', () => this.validateForm());

    // Form submission
    document.getElementById('wd-form').addEventListener('submit', (e) => this.handleSubmit(e));

    // ResizeObserver for responsive recalculation
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.state.uploadedImage) {
          this.recalculateOverlay();
        }
      });
      this.resizeObserver.observe(this.frameContainer);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', () => {
        if (this.state.uploadedImage) {
          this.recalculateOverlay();
        }
      });
    }
  }

  handleFileUpload(e) {
    const file = e.target.files[0];
    const errorDiv = document.getElementById('wd-file-error');
    errorDiv.classList.add('wd-hidden');

    if (!file) return;

    // Validate file type
    if (!this.config.allowedTypes.includes(file.type)) {
      errorDiv.textContent = 'Invalid file type. Please upload JPG, PNG, or WEBP.';
      errorDiv.classList.remove('wd-hidden');
      return;
    }

    // Validate file size
    if (file.size > this.config.maxFileSize) {
      errorDiv.textContent = 'File too large. Maximum size is 5MB.';
      errorDiv.classList.remove('wd-hidden');
      return;
    }

    // Read file
    const reader = new FileReader();
    reader.onload = (event) => {
      this.uploadedImage.onload = () => {
        this.state.uploadedImage = this.uploadedImage;
        this.uploadedImage.style.display = 'block';
        this.overlay.style.display = 'block';
        
        // Initial overlay setup
        this.recalculateOverlay();
        
        // Set initial position (centered-ish)
        this.setPositionFromNormalized(0.35, 0.4);
        
        this.validateForm();
      };
      this.uploadedImage.src = event.target.result;
      this.state.imageData = event.target.result;
    };
    reader.readAsDataURL(file);
  }

  // Calculate overlay size in pixels based on mm ratios
  computeOverlaySize(frameRect, watchModel) {
    const card = this.config.cardDimensions;
    const watch = this.config.watchModels[watchModel];
    
    // Calculate which dimension of the frame is constraining (due to aspect ratio + object-fit: contain)
    const frameAspect = frameRect.width / frameRect.height;
    const cardAspect = card.width / card.height;
    
    let cardDisplayWidth, cardDisplayHeight;
    
    if (frameAspect > cardAspect) {
      // Frame is wider than card - height is constraining
      cardDisplayHeight = frameRect.height;
      cardDisplayWidth = cardDisplayHeight * cardAspect;
    } else {
      // Frame is taller than card - width is constraining
      cardDisplayWidth = frameRect.width;
      cardDisplayHeight = cardDisplayWidth / cardAspect;
    }
    
    // Calculate watch overlay size based on mm ratios
    const watchWidthPx = cardDisplayWidth * (watch.width / card.width);
    const watchHeightPx = cardDisplayHeight * (watch.height / card.height);
    
    return {
      width: watchWidthPx,
      height: watchHeightPx,
      // Also return offset for centering (if frame is larger than card display)
      offsetX: (frameRect.width - cardDisplayWidth) / 2,
      offsetY: (frameRect.height - cardDisplayHeight) / 2,
      cardDisplayWidth,
      cardDisplayHeight
    };
  }

  // Apply calculated size to overlay element
  applyOverlaySize(size) {
    this.overlay.style.width = `${size.width}px`;
    this.overlay.style.height = `${size.height}px`;
    
    // Store for position calculations
    this.currentOverlaySize = size;
  }

  // drawOverlayOnCanvas(ctx, canvas, x, y, w, h) {
  //   // Read values from your config (matches CSS)
  //   const fillRGBA = this.config.overlayStyle.fillRGBA;        // rgba(0,0,0,0.3)
  //   const outlineColor = this.config.overlayStyle.outlineColor; // #030201

  //   // IMPORTANT: outlinePx in config is CSS px (screen px), NOT export px.
  //   // Convert CSS px -> export px using the ratio between the natural image width
  //   // and the displayed card display width.
  //   const cssOutlinePx = this.config.overlayStyle.outlinePx; // e.g. 20 or 25

  //   // Scale factor from displayed card area -> natural image pixels
  //   const scale = canvas.width / this.currentOverlaySize.cardDisplayWidth;
  //   const outlinePx = cssOutlinePx * scale;

  //   // 1) Draw inner semi-transparent fill (should stay transparent)
  //   ctx.fillStyle = fillRGBA;
  //   ctx.fillRect(x, y, w, h);

  //   // 2) Draw outer ring as 4 rectangles (mimics CSS outline exactly)
  //   ctx.fillStyle = outlineColor;

  //   // Top
  //   ctx.fillRect(x - outlinePx, y - outlinePx, w + outlinePx * 2, outlinePx);
  //   // Bottom
  //   ctx.fillRect(x - outlinePx, y + h, w + outlinePx * 2, outlinePx);
  //   // Left
  //   ctx.fillRect(x - outlinePx, y, outlinePx, h);
  //   // Right
  //   ctx.fillRect(x + w, y, outlinePx, h);
  // }

  getNormalizedPosition() {
    if (!this.currentOverlaySize) return { x: 0.35, y: 0.4 };

    const leftPx = parseFloat(this.overlay.style.left || '0');
    const topPx  = parseFloat(this.overlay.style.top  || '0');

    const xPx = leftPx - this.currentOverlaySize.offsetX;
    const yPx = topPx  - this.currentOverlaySize.offsetY;

    return {
      x: xPx / this.currentOverlaySize.cardDisplayWidth,
      y: yPx / this.currentOverlaySize.cardDisplayHeight
    };
  }

  // // Test to make sure snapdom loaded correctly
  // if (typeof snapdom !== "function") {
  //   console.error("snapdom not loaded");
  //   alert("Export tool failed to load. Please refresh and try again.");
  //   return;
  // }

  // // ChatGPT helper function
  // getExportOutlinePx(canvas) {
  //   const { outlineCssPx } = this.config.overlayStyle;

  //   // How big is the image displayed on screen (CSS pixels)?
  //   const imgRect = this.uploadedImage.getBoundingClientRect();

  //   // Convert "20 CSS px" into "X image px"
  //   // Example: canvas.width=2000px, imgRect.width=500px => scale=4 => outline=80px
  //   const scaleX = canvas.width / imgRect.width;

  //   return outlineCssPx * scaleX;
  // }

  // Set position from normalized percentages
  setPositionFromNormalized(xPct, yPct) {
    if (!this.currentOverlaySize) return;
    
    const xPx = (xPct * this.currentOverlaySize.cardDisplayWidth) + this.currentOverlaySize.offsetX;
    const yPx = (yPct * this.currentOverlaySize.cardDisplayHeight) + this.currentOverlaySize.offsetY;
    
    this.overlay.style.left = `${xPx}px`;
    this.overlay.style.top = `${yPx}px`;
  }

  // Recalculate and reapply overlay on resize or model change
  recalculateOverlay() {
    if (!this.state.uploadedImage) return;
    
    const frameRect = this.frameContainer.getBoundingClientRect();
    const size = this.computeOverlaySize(frameRect, this.state.watchModel);
    
    // Get current normalized position before resizing
    const normalizedPos = this.getNormalizedPosition();
    
    // Apply new size
    this.applyOverlaySize(size);
    
    // Reapply position with new size
    this.setPositionFromNormalized(normalizedPos.x, normalizedPos.y);
    
    // Reapply rotation
    this.overlay.style.transform = `rotate(${this.state.rectangleRotation}deg)`;
  }

  startDrag(e) {
    if (!this.state.uploadedImage) return;

    e.preventDefault();
    this.overlay.setPointerCapture?.(e.pointerId);

    const frameRect = this.frameContainer.getBoundingClientRect();
    const leftPx = parseFloat(this.overlay.style.left || '0');
    const topPx  = parseFloat(this.overlay.style.top  || '0');

    // Offset between pointer and overlay's top-left (unrotated)
    this.state.isDragging = true;
    this.state.dragStartX = e.clientX - (frameRect.left + leftPx);
    this.state.dragStartY = e.clientY - (frameRect.top  + topPx);
  }

  drag(e) {
    if (!this.state.isDragging) return;

    e.preventDefault();

    const frameRect = this.frameContainer.getBoundingClientRect();

    // Calculate new position (unrotated)
    let newLeft = e.clientX - frameRect.left - this.state.dragStartX;
    let newTop  = e.clientY - frameRect.top  - this.state.dragStartY;

    // Constrain to card display area (use unrotated overlay size)
    const minX = this.currentOverlaySize.offsetX;
    const minY = this.currentOverlaySize.offsetY;

    const overlayW = this.currentOverlaySize.width;
    const overlayH = this.currentOverlaySize.height;

    const maxX = minX + this.currentOverlaySize.cardDisplayWidth  - overlayW;
    const maxY = minY + this.currentOverlaySize.cardDisplayHeight - overlayH;

    newLeft = Math.max(minX, Math.min(maxX, newLeft));
    newTop  = Math.max(minY, Math.min(maxY, newTop));

    this.overlay.style.left = `${newLeft}px`;
    this.overlay.style.top  = `${newTop}px`;
  }

  endDrag(e) {
    this.state.isDragging = false;

    // Optional cleanup
    if (e?.pointerId != null) {
      this.overlay.releasePointerCapture?.(e.pointerId);
    }
  }

  validateForm() {
    const sourceLink = document.getElementById('wd-source-link').value.trim();
    const name = document.getElementById('wd-name').value.trim();
    const email = document.getElementById('wd-email').value.trim();
    const hasImage = this.state.uploadedImage !== null;
    const submitBtn = document.getElementById('wd-submit-btn');

    // Validate source link
    const sourceError = document.getElementById('wd-source-error');
    sourceError.classList.add('wd-hidden');

    let sourceValid = false;
    if (sourceLink) {
      // Check if it's a URL or "Title - Ch. #" format
      const isURL = sourceLink.startsWith('http://') || sourceLink.startsWith('https://');
      const isMangaFormat = /^.+\s*-\s*ch\.?\s*\d+/i.test(sourceLink);
      
      if (isURL || isMangaFormat) {
        sourceValid = true;
      } else {
        sourceError.textContent = 'Please provide a valid URL or format as "Title - Ch. #"';
        sourceError.classList.remove('wd-hidden');
      }
    }

    const isValid = hasImage && sourceValid && name && email && this.validateEmail(email);
    submitBtn.disabled = !isValid;

    const downloadBtn = document.getElementById('wd-download-btn');
    downloadBtn.disabled = !hasImage;
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  drawRotatedOverlayOnCapturedCanvas(captureCanvas) {
    const ctx = captureCanvas.getContext("2d");

    // Rotation in radians
    const angle = (this.state.rectangleRotation || 0) * Math.PI / 180;
    if (!angle) return; // no rotation, nothing to do

    // Overlay position/size in LIVE widget pixels
    // (we use style.left/top, because those match your normalized math)
    const left = parseFloat(this.overlay.style.left || "0");
    const top  = parseFloat(this.overlay.style.top  || "0");

    const w = this.currentOverlaySize?.width  || this.overlay.getBoundingClientRect().width;
    const h = this.currentOverlaySize?.height || this.overlay.getBoundingClientRect().height;

    // Get overlay center (in captureCanvas coordinates)
    const cx = left + w / 2;
    const cy = top  + h / 2;

    // Use the same fill as your live widget
    // (match your CSS background rgba)
    const fillRGBA = "rgba(0, 0, 0, 0.3)";

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.translate(-w / 2, -h / 2);

    // Draw the gray window
    ctx.fillStyle = fillRGBA;
    ctx.fillRect(0, 0, w, h);

    ctx.restore();
  }

  async handleSubmit(e) {
    e.preventDefault();

    // Check honeypot
    if (e.target.website.value) {
      console.warn('Honeypot triggered');
      return;
    }

    const submitBtn = document.getElementById('wd-submit-btn');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    try {
      // Keep placement numbers in sync with what the user sees
      const normalizedPos = this.getNormalizedPosition();

      // Pixel-perfect WYSIWYG preview (from SnapDOM)
      const previewDataURL = await this.capturePreviewDataURL();

      const payload = {
        name: document.getElementById('wd-name').value.trim(),
        email: document.getElementById('wd-email').value.trim(),
        sourceLink: document.getElementById('wd-source-link').value.trim(),
        // template: this.state.template,
        watchModel: this.state.watchModel,
        notes: document.getElementById('wd-notes').value.trim(),
        placement: {
          x: normalizedPos.x,
          y: normalizedPos.y,
          rotation: this.state.rectangleRotation,
          overlayWidthMm: this.config.watchModels[this.state.watchModel].width,
          overlayHeightMm: this.config.watchModels[this.state.watchModel].height,
          cardWidthMm: this.config.cardDimensions.width,
          cardHeightMm: this.config.cardDimensions.height
        },
        previewImage: previewDataURL,
        originalImage: this.state.imageData
      };

      // Submit to serverless endpoint
      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Submission failed');

      // Show confirmation
      document.getElementById('wd-form').classList.add('wd-hidden');
      document.getElementById('wd-confirmation').classList.remove('wd-hidden');

    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again or contact us directly.');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit for Quote';
    }
  }

  async capturePreviewDataURL() {
    if (!this.state.uploadedImage) throw new Error("No uploaded image");

    const result = await snapdom(this.frameContainer, {
      scale: 1,
      dpr: 1,
      outerTransforms: true,
      outerShadows: true
    });

    let dataUrl;

    // Some SnapDOM builds return a string (data URL) if asDataUrl works
    try {
      const maybe = await result.toPng({ asDataUrl: true });
      if (typeof maybe === "string" && maybe.startsWith("data:image")) {
        dataUrl = maybe;
      }
    } catch (_) {
      // ignore and fall back
    }

    // Fallback: toPng() returns an <img> element
    if (!dataUrl) {
      const imgEl = await result.toPng();
      dataUrl = imgEl?.src; // this SHOULD be a data:image/png;base64,... URL
    }

    result.destroy?.();

    if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
      throw new Error("SnapDOM did not return a PNG data URL.");
    }

    return dataUrl;
  }

  async downloadPreview() {
    if (!this.state.uploadedImage) {
      alert("Please upload an image first");
      return;
    }

    try {
      const dataUrl = await this.capturePreviewDataURL();

      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `watch-slabs-custom-design-preview-${timestamp}.png`;

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download preview failed:", err);
      alert("Preview export failed. Please try again.");
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new WatchDesigner('watch-designer-container');
  });
} else {
  new WatchDesigner('watch-designer-container');
}